import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import axiosInstance from "./axiosInstance";

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface SearchParams extends PaginationParams {
  keyword?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  totalRecord: number;
}

interface PaginatedSingleResponse<T> {
  data: T;
  totalRecord: number;
}

export function useCrudApi<T>(resource: string) {
  const queryClient = useQueryClient();
  const baseUrl = `/api/${resource}`;

  // Hook quản lý phân trang
  const usePaginationQuery = (defaultPage = 1, defaultPageSize = 10) => {
    const [page, setPage] = useState(defaultPage);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const handlePageChange = (newPage: number) => setPage(newPage);
    const handlePageSizeChange = (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(1); // reset về page 1 khi đổi pageSize
    };

    return { page, pageSize, handlePageChange, handlePageSizeChange };
  };

  const useList = (
    params: PaginationParams & { enabled?: boolean } = {
      page: 1,
      pageSize: 10,
      enabled: true,
    }
  ) => {
    const { page, pageSize, enabled } = params;
    return useQuery({
      queryKey: [resource, "list", params],
      queryFn: async () => {
        const { data } = await axiosInstance.get<PaginatedResponse<T>>(
          baseUrl,
          { params: { page, pageSize } }
        );
        return data;
      },
      enabled: enabled,
    });
  };

  const useGetById = (
    id: string | number,
    options?: Omit<
      UseQueryOptions<T, AxiosError, T, QueryKey>,
      "queryKey" | "queryFn"
    >
  ) => {
    return useQuery<T, AxiosError, T, QueryKey>({
      queryKey: [resource, "detail", id],
      queryFn: async () => {
        const { data } = await axiosInstance.get<PaginatedSingleResponse<T>>(
          `${baseUrl}/${id}`
        );
        return data.data;
      },
      enabled: !!id,
      ...options,
    });
  };

  const useCreate = (
    options?: UseMutationOptions<T, AxiosError, Partial<T>>
  ) => {
    return useMutation<T, AxiosError, Partial<T>>({
      mutationFn: async (newData: Partial<T>) => {
        const { data } = await axiosInstance.post<T>(baseUrl, newData);
        return data;
      },
      onSuccess: (...args) => {
        queryClient.invalidateQueries({ queryKey: [resource, "list"] });
        options?.onSuccess?.(...args);
      },
      ...options,
    });
  };

  const useUpdate = (
    options?: UseMutationOptions<T, AxiosError, Partial<T>>
  ) => {
    return useMutation<T, AxiosError, Partial<T>>({
      mutationFn: async (updatedData: Partial<T>) => {
        const { data } = await axiosInstance.put<T>(baseUrl, updatedData);
        return data;
      },
      onSuccess: (...args) => {
        queryClient.invalidateQueries({ queryKey: [resource, "list"] });
        options?.onSuccess?.(...args);
      },
      ...options,
    });
  };

  const useDelete = (
    options?: UseMutationOptions<any, AxiosError, string | number>
  ) => {
    return useMutation<any, AxiosError, string | number>({
      mutationFn: async (id: string | number) => {
        const { data } = await axiosInstance.delete(`${baseUrl}/${id}`);
        return data;
      },
      onSuccess: (...args) => {
        queryClient.invalidateQueries({
          queryKey: [resource, "search"],
          exact: false,
        });
        options?.onSuccess?.(...args);
      },
      ...options,
    });
  };

  // Debounce cho Search API
  const useSearch = (
    keywords: string,
    params: PaginationParams = { page: 1, pageSize: 10 },
    options?: Omit<
      UseQueryOptions<
        PaginatedResponse<T>,
        AxiosError,
        PaginatedResponse<T>,
        QueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const debouncedKeywords = useDebouncedValue(keywords, 500); // 500ms debounce
    return useQuery<
      PaginatedResponse<T>,
      AxiosError,
      PaginatedResponse<T>,
      QueryKey
    >({
      queryKey: [resource, "search", debouncedKeywords, params],
      queryFn: async () => {
        const { data } = await axiosInstance.post<PaginatedResponse<T>>(
          `${baseUrl}/search`,
          {
            keyword: debouncedKeywords,
            ...params,
          }
        );
        return data;
      },
      ...options,
    });
  };

  // Hook nhỏ để debounce value
  const useDebouncedValue = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    const debounced = debounce((newValue: string) => {
      setDebouncedValue(newValue);
    }, delay);

    // Update debounce khi value thay đổi
    useEffect(() => {
      debounced(value);
      return () => debounced.cancel();
    }, [value, debounced]);

    return debouncedValue;
  };

  return {
    useList,
    useGetById,
    useCreate,
    useUpdate,
    useDelete,
    useSearch,
    usePaginationQuery,
  };
}
